/**
 * PRpillar fork: named stack groups for grouped stacked bars.
 *
 * A cartesian chart can hold several bar stacks side by side on the same
 * category (one per `series.stack`), e.g. `all / qualified / won`, each split
 * by the same pivot. Upstream Lightdash renders them, but nothing on the chart
 * says which bar is which stack, and the axis tooltip lists the same series
 * names once per stack without saying which block belongs to which stack.
 *
 * `eChartsConfig.stackGroupLabels` maps a stack name to a display label. When
 * it is set, a label is drawn over every bar of that stack and the tooltip is
 * split into one block per stack, headed by the label, with empty and zero
 * values dropped. Charts without the setting are rendered exactly as before.
 */
import { toNumber } from 'lodash';
import { GRAY_2, GRAY_6, GRAY_7 } from './styles/themeColors';
export const hasStackGroupLabels = (labels) => !!labels &&
    typeof labels === 'object' &&
    Object.values(labels).some((label) => typeof label === 'string' && label.trim() !== '');
/** Label for a stack, or undefined when the stack is not a named group. */
export const getStackGroupLabel = (labels, stack) => {
    if (!stack || !hasStackGroupLabels(labels))
        return undefined;
    const label = labels[stack];
    return typeof label === 'string' && label.trim() !== '' ? label : undefined;
};
/** Null, undefined, empty string, NaN and zero count as "no value". */
export const isEmptyStackGroupValue = (value) => {
    if (value === undefined || value === null || value === '')
        return true;
    if (typeof value === 'number')
        return value === 0 || Number.isNaN(value);
    if (typeof value === 'string') {
        const numeric = toNumber(value);
        return !Number.isNaN(numeric) && numeric === 0;
    }
    return false;
};
const escapeHtml = (text) => text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
export const formatStackGroupHeader = (label, isFirst) => {
    const styles = [
        `color: ${GRAY_7}`,
        'font-weight: 600',
        'font-size: 12px',
        `margin: ${isFirst ? '0' : '6px'} 0 2px 0`,
        `padding-top: ${isFirst ? '0' : '4px'}`,
        isFirst ? '' : `border-top: 1px solid ${GRAY_2}`,
    ]
        .filter(Boolean)
        .join('; ');
    return `<div style="${styles}">${escapeHtml(label)}</div>`;
};
const formatStackGroupEmpty = () => `<div style="color: ${GRAY_6}; font-size: 12px; margin-bottom: 2px">—</div>`;
/**
 * Re-assemble already rendered tooltip rows into one block per named stack.
 *
 * `rowHtmls[i]` and `rowValues[i]` belong to `params[i]`. Blocks follow the
 * order in which the stacks first appear in `series` (left to right on the
 * chart). Rows of series outside any named stack keep their place after the
 * blocks, so a line on a second axis is not lost.
 */
export const renderStackGroupedTooltipRows = ({ params, rowHtmls, rowValues, series, labels, }) => {
    const stackOf = (param) => typeof param.seriesIndex === 'number'
        ? series?.[param.seriesIndex]?.stack
        : undefined;
    const groupOrder = [];
    (series ?? []).forEach((serie) => {
        const { stack } = serie;
        if (stack &&
            !groupOrder.includes(stack) &&
            getStackGroupLabel(labels, stack)) {
            groupOrder.push(stack);
        }
    });
    const grouped = new Map(groupOrder.map((stack) => [stack, []]));
    const ungrouped = [];
    params.forEach((param, index) => {
        const html = rowHtmls[index];
        if (!html)
            return;
        const stack = stackOf(param);
        const bucket = stack ? grouped.get(stack) : undefined;
        if (bucket) {
            if (isEmptyStackGroupValue(rowValues[index]))
                return;
            bucket.push(html);
        }
        else {
            ungrouped.push(html);
        }
    });
    const blocks = groupOrder.map((stack, index) => {
        const rows = grouped.get(stack) ?? [];
        return `${formatStackGroupHeader(getStackGroupLabel(labels, stack) ?? stack, index === 0)}${rows.length > 0 ? rows.join('') : formatStackGroupEmpty()}`;
    });
    return `${blocks.join('')}${ungrouped.join('')}`;
};
const getStackGroupLabelPlacement = (flipAxis, rotate) => {
    if (flipAxis)
        return { position: 'right' };
    if (rotate === 90) {
        return {
            position: 'top',
            rotate: 90,
            align: 'left',
            verticalAlign: 'middle',
            distance: 6,
        };
    }
    return {
        position: 'top',
        rotate: 0,
        align: 'center',
        verticalAlign: 'bottom',
        distance: 4,
    };
};
export const getStackGroupLabelContent = (content) => content === 'label' || content === 'total' ? content : 'both';
export const getStackGroupLabelText = ({ groupLabel, formattedTotal, showTotal, content = 'both', }) => {
    if (content === 'total')
        return formattedTotal;
    if (content === 'label')
        return groupLabel;
    return showTotal && formattedTotal
        ? `${groupLabel} · ${formattedTotal}`
        : groupLabel;
};
/**
 * Text and placement for the synthetic label series that sits on top of a
 * named stack. The label reads `Won`, or `Won · 12` when stack totals are on.
 * Vertical bars get a horizontal label by default (the fitter below keeps it
 * inside the bar slot); `rotate: 90` brings back the upright label of the
 * first version. Overlap hiding is off: a group label must never vanish.
 */
export const getStackGroupLabelOptions = ({ groupLabel, formattedTotal, showTotal, flipAxis, rotate = 0, content = 'both', }) => ({
    text: getStackGroupLabelText({
        groupLabel,
        formattedTotal,
        showTotal,
        content,
    }),
    label: getStackGroupLabelPlacement(flipAxis, rotate),
    labelLayout: { hideOverlap: false },
});
/** Placement of the upright variant, for the total-only fitter below. */
export const getStackGroupUprightLabelPlacement = () => getStackGroupLabelPlacement(false, 90);
export const getStackGroupLabelRotate = (rotate) => (Number(rotate) === 90 ? 90 : 0);
/**
 * Horizontal group labels have to fit the bar they sit on. When the full
 * label is wider than the bar slot, the chart falls back tier by tier:
 *
 * 1. the full label (`stackGroupLabels`), font shrunk down to the minimum;
 * 2. the short label (`stackGroupShortLabels`, optional, e.g. `Qual.`);
 * 3. the initial of the full label (`A / Q / W`).
 *
 * The choice is made for the whole chart at once from the bar width, so all
 * stacks always use the same tier and font size, and it is re-made on every
 * resize (ECharts calls `labelLayout` again).
 */
export const STACK_GROUP_LABEL_MIN_FONT_SIZE = 10;
/** ECharts default `barGap`: the space between bars of one category. */
const STACK_GROUP_BAR_GAP_RATIO = 0.3;
/** Air kept between two neighbouring labels. */
const STACK_GROUP_LABEL_SPACING_PX = 4;
/** Anything drawn with this layout is off canvas, i.e. hidden. */
const STACK_GROUP_HIDDEN_LAYOUT = { x: -10000, y: -10000 };
export const getStackGroupInitial = (label) => (Array.from(label.trim())[0] ?? '').toUpperCase();
/** Texts per tier for one stack: full, then short (if any), then initial. */
export const getStackGroupLabelTiers = (labels, shortLabels, stack, withShortTier) => {
    const full = getStackGroupLabel(labels, stack);
    if (!full)
        return [];
    const short = getStackGroupLabel(shortLabels, stack) ?? full;
    return withShortTier
        ? [full, short, getStackGroupInitial(full)]
        : [full, getStackGroupInitial(full)];
};
export const hasStackGroupShortLabels = (labels, shortLabels, stacks) => stacks.some((stack) => !!getStackGroupLabel(labels, stack) &&
    !!getStackGroupLabel(shortLabels, stack));
/**
 * Pick the first tier whose widest label fits `available` px, at the largest
 * font size not above `baseFontSize` and not below `minFontSize`. When even
 * the last tier does not fit, it is used at the minimum size.
 */
export const pickStackGroupLabelFit = ({ tierWidths, available, baseFontSize, minFontSize = STACK_GROUP_LABEL_MIN_FONT_SIZE, }) => {
    for (let tier = 0; tier < tierWidths.length; tier += 1) {
        const width = tierWidths[tier];
        if (width <= available)
            return { tier, fontSize: baseFontSize };
        const fontSize = Math.floor((baseFontSize * available) / width);
        if (fontSize >= minFontSize)
            return { tier, fontSize };
    }
    return {
        tier: Math.max(0, tierWidths.length - 1),
        fontSize: minFontSize,
    };
};
let canvasContext;
/**
 * Text width at a font size. Uses a canvas in the browser; elsewhere (tests,
 * server) a rough per-character estimate. Either way the fitter calibrates it
 * against the real label box ECharts reports, so only proportions matter.
 */
export const measureStackGroupLabel = (text, fontSize) => {
    if (canvasContext === undefined) {
        try {
            const doc = globalThis.document;
            canvasContext =
                doc?.createElement('canvas').getContext?.('2d') ?? null;
        }
        catch {
            canvasContext = null;
        }
    }
    if (canvasContext) {
        canvasContext.font = `500 ${fontSize}px sans-serif`;
        return canvasContext.measureText(text).width;
    }
    return text.length * fontSize * 0.6;
};
/**
 * One fitter per chart. Every named stack gets one synthetic label series per
 * tier; each series asks the fitter through `labelLayout(tier)` whether it is
 * the tier to show and at which size. The losing tiers are moved off canvas.
 */
export const createStackGroupLabelFitter = ({ tiers, suffix = '', baseFontSize, minFontSize = STACK_GROUP_LABEL_MIN_FONT_SIZE, measure = measureStackGroupLabel, fixedTierWidths = [], }) => {
    const tierCount = Math.max(0, ...tiers.map((texts) => texts.length));
    let scale;
    const fits = new Map();
    const fitFor = (params, tier) => {
        const barWidth = params.rect?.width ?? 0;
        if (scale === undefined &&
            fixedTierWidths[tier] === undefined &&
            params.text &&
            params.labelRect?.width) {
            const estimate = measure(params.text, baseFontSize);
            if (estimate > 0)
                scale = params.labelRect.width / estimate;
        }
        const cached = fits.get(barWidth);
        if (cached)
            return cached;
        const tierWidths = Array.from({ length: tierCount }, (_, index) => fixedTierWidths[index] ??
            Math.max(0, ...tiers.map((texts) => measure(`${texts[Math.min(index, texts.length - 1)]}${suffix}`, baseFontSize) * (scale ?? 1))));
        const fit = pickStackGroupLabelFit({
            tierWidths,
            available: barWidth * (1 + STACK_GROUP_BAR_GAP_RATIO) -
                STACK_GROUP_LABEL_SPACING_PX,
            baseFontSize,
            minFontSize,
        });
        fits.set(barWidth, fit);
        return fit;
    };
    return {
        tierCount,
        labelLayout: (tier) => (params) => {
            const fit = fitFor(params, tier);
            return fit.tier === tier
                ? { hideOverlap: false, fontSize: fit.fontSize }
                : { hideOverlap: false, ...STACK_GROUP_HIDDEN_LAYOUT };
        },
    };
};
/**
 * Total-only labels (`stackGroupLabelContent: total`) on vertical bars: the
 * widest total of the chart is laid horizontally when it fits the bar slot
 * (font shrunk down to the minimum), otherwise every total turns upright.
 * Two tiers per stack: 0 is horizontal, 1 is upright, one line wide.
 */
export const createStackTotalLabelFitter = ({ totals, baseFontSize, minFontSize = STACK_GROUP_LABEL_MIN_FONT_SIZE, measure = measureStackGroupLabel, }) => {
    const widest = totals.reduce((best, text) => measure(text, baseFontSize) > measure(best, baseFontSize)
        ? text
        : best, '');
    return createStackGroupLabelFitter({
        tiers: [[widest, widest]],
        baseFontSize,
        minFontSize,
        measure,
        fixedTierWidths: [undefined, Math.ceil(baseFontSize * 1.3)],
    });
};
//# sourceMappingURL=stackGroups.js.map